import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Box, Typography, CircularProgress, Alert, Button, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const fmtDate = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
};

const calcAgeDays = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

const PinMonitoringList = ({ posConfig, embedded }) => {
    const navigate = useNavigate();
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sendingReminder, setSendingReminder] = useState(null);
    const [reminderSuccess, setReminderSuccess] = useState(null);
    const [reminderError, setReminderError] = useState(null);

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);

            if (msg.task === 'respGetPendingRequests') {
                if (msg.status === 'succeeded') {
                    if (msg.nextTokenUsed) {
                        setRequests((prev) => [...prev, ...(msg.requests || [])]);
                    } else {
                        setRequests(msg.requests || []);
                    }
                    setNextToken(msg.nextToken || null);
                    setError(null);
                } else {
                    setError(msg.message || 'Fehler beim Laden.');
                }
                setLoading(false);
                setLoadingMore(false);
                return;
            }

            if (msg.task === 'respSendPinReminder') {
                setSendingReminder(null);
                if (msg.status === 'succeeded') {
                    setReminderSuccess(msg.pinRecordId);
                    setReminderError(null);
                    if (msg.smsWarning) {
                        setReminderError(msg.smsWarning);
                    }
                    setTimeout(() => setReminderSuccess(null), 3000);
                } else {
                    setReminderError(msg.message || 'Fehler beim Senden.');
                    setTimeout(() => setReminderError(null), 5000);
                }
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, []);

    const loadRequests = useCallback((opts = {}) => {
        const token = opts.nextToken || null;
        if (token) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setRequests([]);
        }
        setNextToken(null);
        sendMessage(JSON.stringify({
            task: 'getPendingRequests',
            station: cookies.station,
            staff: cookies.staff,
            nextToken: token || undefined,
        }));
    }, [sendMessage, cookies.station, cookies.staff]);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) loadRequests();
    }, [readyState]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadMore = () => {
        if (nextToken) loadRequests({ nextToken });
    };

    const handleSendReminder = (pinRecordId) => {
        setSendingReminder(pinRecordId);
        setReminderSuccess(null);
        setReminderError(null);
        sendMessage(JSON.stringify({
            task: 'sendPinReminder',
            station: cookies.station,
            staff: cookies.staff,
            pinRecordId,
        }));
    };

    // Sort by age (oldest first) — already sorted by backend, but ensure client-side too
    const sorted = [...requests].sort((a, b) => {
        const dateA = new Date(a.erstelltAm).getTime();
        const dateB = new Date(b.erstelltAm).getTime();
        return dateA - dateB;
    });

    return (
        <Box sx={{ minHeight: embedded ? 'auto' : '100vh' }}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', px: embedded ? 0 : 2.5, py: embedded ? 0 : 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                {!embedded && (
                    <>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate('..')}
                        sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#555' }}
                    >
                        Zurück
                    </Button>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Monitoring – Ausstehende PIN-Anforderungen
                    </Typography>
                    </>
                )}
                </Box>

                {reminderError && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{reminderError}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : sorted.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: '#999', fontSize: 14 }}>
                        Keine ausstehenden PIN-Anforderungen.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {sorted.map((req) => {
                                const ageDays = calcAgeDays(req.erstelltAm);
                                const ageLabel = ageDays === 0 ? 'Heute' : ageDays === 1 ? '1 Tag' : `${ageDays} Tage`;
                                const isOld = ageDays > 3;

                                return (
                                    <Box key={req.pinRecordId} sx={{
                                        display: 'flex', alignItems: 'center', gap: 2,
                                        px: 2, py: 1.5, borderRadius: 2,
                                        bgcolor: '#fff', border: '1px solid #eee',
                                        '&:hover': { bgcolor: '#f7f7f5', borderColor: '#ddd' },
                                    }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', minWidth: 70 }}>
                                            {req.kdnr}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#555', minWidth: 120 }}>
                                            {req.customerName || '–'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: '#777', minWidth: 100 }}>
                                            {req.sendungsnummer || '–'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: '#777', minWidth: 80 }}>
                                            {req.spedition || '–'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#999', minWidth: 100 }}>
                                            {fmtDate(req.erstelltAm)}
                                        </Typography>
                                        <Chip
                                            label={ageLabel}
                                            size="small"
                                            sx={{
                                                height: 20, fontSize: 10, fontWeight: 700,
                                                bgcolor: isOld ? '#fef3c7' : '#e5e5e3',
                                                color: isOld ? '#92400e' : '#555',
                                            }}
                                        />
                                        <Box sx={{ flex: 1 }} />
                                        {reminderSuccess === req.pinRecordId ? (
                                            <Chip label="Gesendet ✓" size="small"
                                                sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#d1fae5', color: '#065f46' }} />
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disableElevation
                                                disabled={sendingReminder === req.pinRecordId}
                                                onClick={() => handleSendReminder(req.pinRecordId)}
                                                sx={{
                                                    borderRadius: 2, textTransform: 'none', fontSize: 11, fontWeight: 600,
                                                    borderColor: '#ddd', color: '#1a1a1a', minWidth: 0,
                                                }}
                                            >
                                                {sendingReminder === req.pinRecordId
                                                    ? <CircularProgress size={14} />
                                                    : 'Erinnerung senden'}
                                            </Button>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {nextToken && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disableElevation
                                    disabled={loadingMore}
                                    onClick={handleLoadMore}
                                    sx={{
                                        borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                        borderColor: '#ddd', color: '#1a1a1a',
                                    }}
                                >
                                    {loadingMore ? <CircularProgress size={16} /> : 'Mehr laden'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PinMonitoringList;
